import React from 'react';
import { X, Link, Linkedin } from 'lucide-react';
import { useTheme } from './ThemeContext';

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose }) => {
    const { theme } = useTheme();

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-all duration-200">
            <div className={`relative w-full max-w-lg rounded-2xl shadow-2xl p-6 transform transition-all scale-100 ${theme === 'dark' ? 'bg-[#1e1e1e] border border-gray-700' : 'bg-white'
                }`}>
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Share Research
                    </h3>
                    <button
                        onClick={onClose}
                        className={`p-1 rounded-full transition-colors ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                            }`}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content Preview */}
                <div className={`rounded-xl p-5 mb-8 ${theme === 'dark' ? 'bg-[#2d2d2d]' : 'bg-gray-100'}`}>
                    <div className="space-y-2 font-mono text-sm leading-relaxed">
                        <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                            <span className="text-blue-400">Query: </span>
                            "Latest Supreme Court rulings on commercial arbitration in India..."
                        </div>
                        <div className={`h-px w-full my-3 ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
                        <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                            Based on the Arbitration and Conciliation Act, 1996, the recent trends indicate...
                            <span className="opacity-50"> [Read More]</span>
                        </div>
                    </div>
                    <div className={`mt-3 text-xs text-right ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        VerdictAI Shared Chat
                    </div>
                </div>

                {/* Sharing Options */}
                <div className="grid grid-cols-4 gap-4">
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(window.location.href);
                            // Optional: You could add a toast notification here
                            alert("Link copied to clipboard!");
                        }}
                        className="flex flex-col items-center gap-2 group"
                    >
                        <div className={`p-3 rounded-full transition-all ${theme === 'dark' ? 'bg-gray-700 group-hover:bg-gray-600' : 'bg-gray-100 group-hover:bg-gray-200'
                            }`}>
                            <Link size={20} className={theme === 'dark' ? 'text-white' : 'text-gray-700'} />
                        </div>
                        <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Copy Link</span>
                    </button>

                    <button className="flex flex-col items-center gap-2 group">
                        <div className={`p-3 rounded-full transition-all ${theme === 'dark' ? 'bg-gray-700 group-hover:bg-gray-600' : 'bg-gray-100 group-hover:bg-gray-200'
                            }`}>
                            <X size={20} className={theme === 'dark' ? 'text-white' : 'text-gray-700'} />
                        </div>
                        <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>X</span>
                    </button>

                    <button className="flex flex-col items-center gap-2 group">
                        <div className={`p-3 rounded-full transition-all ${theme === 'dark' ? 'bg-gray-700 group-hover:bg-gray-600' : 'bg-gray-100 group-hover:bg-gray-200'
                            }`}>
                            <Linkedin size={20} className={theme === 'dark' ? 'text-white' : 'text-gray-700'} />
                        </div>
                        <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>LinkedIn</span>
                    </button>

                    <button className="flex flex-col items-center gap-2 group">
                        <div className={`p-3 rounded-full transition-all ${theme === 'dark' ? 'bg-gray-700 group-hover:bg-gray-600' : 'bg-gray-100 group-hover:bg-gray-200'
                            }`}>
                            {/* Reddit icon usually needs a custom SVG or specific library, using Smile as placeholder or generic share */}
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className={theme === 'dark' ? 'text-white' : 'text-gray-700'}
                            >
                                <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913s2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                            </svg>
                        </div>
                        <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Reddit</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
